import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  
  register: (name: string, email: string, password: string) =>
    api.post('/auth/register', { name, email, password }),
  
  getProfile: () => api.get('/auth/me'),
  
  updateProfile: (data: { name?: string; email?: string }) =>
    api.put('/auth/profile', data),
  
  changePassword: (currentPassword: string, newPassword: string) =>
    api.put('/auth/password', { currentPassword, newPassword }),
};

// Sites API
export const sitesAPI = {
  getSites: () => api.get('/sites'),
  
  getSite: (id: string) => api.get(`/sites/${id}`),
  
  createSite: (data: { name: string; url: string }) =>
    api.post('/sites', data),
  
  updateSite: (id: string, data: { name?: string; url?: string; monitoring_enabled?: boolean }) =>
    api.put(`/sites/${id}`, data),
  
  deleteSite: (id: string) => api.delete(`/sites/${id}`),
  
  getSiteStats: (id: string) => api.get(`/sites/${id}/stats`),
};

// Monitoring API
export const monitoringAPI = {
  getMonitoringData: (siteId: string, days?: number) =>
    api.get(`/monitoring/${siteId}`, { params: { days } }),
  
  triggerCheck: (siteId: string) => api.post(`/monitoring/${siteId}/check`),
  
  getMonitoringLogs: (siteId: string, page?: number, limit?: number) =>
    api.get(`/monitoring/${siteId}/logs`, { params: { page, limit } }),
  
  getDashboardOverview: () => api.get('/monitoring'),
};

// Optimization API
export const optimizationAPI = {
  runAudit: (siteId: string) => api.post(`/optimization/${siteId}/audit`),
  
  getHistory: (siteId: string, days?: number) =>
    api.get(`/optimization/${siteId}/history`, { params: { days } }),
  
  getLatest: (siteId: string) => api.get(`/optimization/${siteId}/latest`),
  
  getSpeedAudits: (siteId: string, page?: number, limit?: number) =>
    api.get(`/optimization/${siteId}/speed`, { params: { page, limit } }),
  
  getSeoAudits: (siteId: string, page?: number, limit?: number) =>
    api.get(`/optimization/${siteId}/seo`, { params: { page, limit } }),
};

// Security API
export const securityAPI = {
  runScan: (siteId: string) => api.post(`/security/${siteId}/scan`),
  
  getScans: (siteId: string) => api.get(`/security/${siteId}/scans`),
};

// SEO API
export const seoAPI = {
  getAnalysis: (siteId: string) => api.get(`/seo/${siteId}/analysis`),
};

// Backups API
export const backupsAPI = {
  createBackup: (siteId: string) => api.post(`/backups/${siteId}/create`),
  
  getBackups: (siteId: string) => api.get(`/backups/${siteId}`),
};

// Reports API
export const reportsAPI = {
  generateReport: (siteId: string) => api.get(`/reports/${siteId}`),
};

// Enhanced Monitoring API
export const enhancedMonitoringAPI = {
  getDashboard: () => api.get('/enhanced/dashboard'),
  
  comprehensiveMonitor: (siteId: string) => 
    api.post(`/enhanced/sites/${siteId}/monitor/comprehensive`),
  
  performanceAudit: (siteId: string) => 
    api.post(`/enhanced/sites/${siteId}/performance/audit`),
  
  securityScan: (siteId: string) => 
    api.post(`/enhanced/sites/${siteId}/security/scan`),
  
  seoAnalysis: (siteId: string) => 
    api.post(`/enhanced/sites/${siteId}/seo/analyze`),
  
  getSiteStatus: (siteId: string) => 
    api.get(`/enhanced/sites/${siteId}/status`),
  
  getHistory: (siteId: string, type: string, days?: number) =>
    api.get(`/enhanced/sites/${siteId}/history/${type}`, { params: { days } }),
  
  getWeeklyReport: () => api.get('/enhanced/reports/weekly'),
  
  getReportHistory: () => api.get('/enhanced/reports/history'),
  
  getReport: (reportId: string) => api.get(`/enhanced/reports/${reportId}`),
  
  getNotifications: (unreadOnly?: boolean, limit?: number) =>
    api.get('/enhanced/notifications', { params: { unread: unreadOnly, limit } }),
  
  markNotificationRead: (notificationId: string) =>
    api.put(`/enhanced/notifications/${notificationId}/read`),
  
  markAllNotificationsRead: () => api.put('/enhanced/notifications/read-all'),
  
  getAlertRules: () => api.get('/enhanced/alerts/rules'),
  
  createAlertRule: (data: any) => api.post('/enhanced/alerts/rules', data),
  
  getPreferences: () => api.get('/enhanced/preferences'),
  
  updatePreferences: (data: any) => api.put('/enhanced/preferences', data),
};

// Billing API
export const billingAPI = {
  createPaymentIntent: (amount: number, currency?: string) =>
    api.post('/billing/create-payment-intent', { amount, currency }),
  
  getHistory: () => api.get('/billing/history'),
  
  upgradePlan: (plan: string) => api.post('/billing/upgrade', { plan }),
};

export default api;